import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Loader2, ChevronRight, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { CONTACT } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing details", description: "Please add your name, email and a message.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setDone(true);
      toast({ title: "Message sent", description: "Thanks \u2014 we'll be in touch shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#f3ede3] py-16 lg:py-24">
      <div className="mx-auto max-w-[1200px] px-5">
        <div className="text-center">
          <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-5xl">
            Contact Us
          </h2>
          <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-gray-600">
            Pop in for a chat, give us a call, or drop us a message using the form below and a member
            of the New Space team will get right back to you.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-5">
          {/* Contact info */}
          <div className="space-y-5 lg:col-span-2">
            {[
              { icon: MapPin, label: "Visit us", value: CONTACT.address },
              { icon: Phone, label: "Call us", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
              { icon: Mail, label: "Email us", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
              { icon: Clock, label: "Opening hours", value: "Mon\u2013Fri 9am\u20136pm \u00b7 Sat 10am\u20134pm" },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-4 rounded-2xl border border-[#e5ddd0] bg-white p-5 shadow-sm">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#35a4a4] text-white">
                  <item.icon size={22} />
                </span>                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-400">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className="font-semibold text-[#16323f] hover:text-[#ec008c]">{item.value}</a>
                  ) : (
                    <p className="font-semibold text-[#16323f]">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {done ? (
              <div className="flex h-full flex-col items-center justify-center rounded-sm bg-white p-10 text-center shadow-lg">
                <div className="grid h-16 w-16 place-items-center rounded-full bg-[#35a4a4] text-white">
                  <Check size={32} />
                </div>
                <h3 className="mt-5 font-display text-2xl font-600 text-[#16323f]">Message sent!</h3>
                <p className="mt-2 max-w-sm text-sm text-gray-600">
                  Thanks for getting in touch. A member of the New Space team will reply to you shortly.
                </p>
                <button
                  onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }}
                  className="mt-6 bg-[#16323f] px-8 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#ec008c]"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-[#e5ddd0] bg-white p-6 shadow-lg lg:p-8">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-name">Full name *</Label>
                    <Input id="c-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="c-phone">Phone</Label>
                    <Input id="c-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="07123 456789" />
                  </div>
                </div>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-email">Email *</Label>
                    <Input id="c-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="c-subject">Subject</Label>
                    <Input id="c-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="How can we help?" />
                  </div>
                </div>
                <div className="mt-5 space-y-1.5">
                  <Label htmlFor="c-message">Message *</Label>
                  <Textarea id="c-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us how we can help..." />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#ec008c] py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70 sm:w-auto sm:px-10"
                >
                  {loading ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : (
                    <>Send message <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
